import React, { Fragment,  useContext, useEffect, useState } from "react";
import styles from "./thirdPartyQuestionsToAsk.module.css";
import { Autocomplete, TextField } from "@mui/material";
import { IQuestionsFromSubCategories } from "../../../Models/thirdPartyQuestionnarieModels";
import {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
} from "@mui/material/useAutocomplete/useAutocomplete";
import QuestionTypesToId from "../../../utils/constants/questionTitleToType";
import agent from "../../../API/apiCalls";
import { CommonResponseModel } from "../../../Models/tableDetailsModel";
import {
    IThirdPartyQuestionnaireContext,
    ThirdPartyQuestionnaireContext
} from "../../../Context/thirdPartyQuestionnaireContext";
import { subDomainError } from "../../../utils/constants/errorMessage";

export const ValidateAnswerWithRegex = (regexString: string, value: string) => {
    const regex = new RegExp(regexString);
    return regex.test(value);
};

interface ICommonInputComponentProps {
    question: IQuestionsFromSubCategories;
    UpdateAnswer: (value: string, questionId?: string ) => void;
}

const CheckSubDomainAvailability = async (ans: string): Promise<boolean> => {
    try {
        if (ans !== "") {
            const res: CommonResponseModel<boolean> = await agent.CheckSubDomainAvailability.get(ans);

            if (res.isAxiosError || !res.flagStatus) {
                console.log("Subdomain is not available");
                return false;
            }

            return res.value;
        } else {
            console.log("Subdomain is empty");
            return false;
        }
    } catch (err) {
        console.log(err);
        return false;
    }
};

export const StringValueComponent = (props: ICommonInputComponentProps) => {
    const [answerValue, setAnswerValue] = useState(props.question.answer ?? "");
    const [error, setError] = useState(false);
    const [flagSubDomainError, setFlagSubDomainError] = useState(false);

    const {
        setFlagSubmitDisabled
    } = useContext(ThirdPartyQuestionnaireContext) as IThirdPartyQuestionnaireContext;


    const HandleDomainCheck = async (value: string) => {
        const domainValid = await CheckSubDomainAvailability(value);
        setFlagSubmitDisabled(!domainValid);
        setFlagSubDomainError(!domainValid);
    }

    // Debounce to delay the API call
    useEffect(() => {
        const handler = setTimeout(async () => {
            if(props.question.questionId === QuestionTypesToId.subDomain && !error && answerValue !== ""){
                await HandleDomainCheck(answerValue);
            }
            // Update the answer after 500ms for every case.
            props.UpdateAnswer(answerValue, props.question.questionId);
        }, 500);
        return () => clearTimeout(handler);
    }, [answerValue]);

    const UpdateAnswer = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAnswerValue(e.target.value);
        setError(false);
        setFlagSubDomainError(false);

        if (props.question.questionRegex) {
            const flagValidAnswer = ValidateAnswerWithRegex(props.question.questionRegex, e.target.value);
            setError(!flagValidAnswer);
        }
    };
    const flagErrorVisible = (error || flagSubDomainError);

    return (
        <Fragment>
            <input
                type={"text"}
                value={answerValue}
                onChange={UpdateAnswer}
                disabled={!props.question.isActive}
                className={`${styles.string_input} ${flagErrorVisible ? styles.error_box : ""}`}
            />
            {flagErrorVisible && <span className={styles.error_message}>{
                flagSubDomainError ? subDomainError : props.question.questionValidationMessage ?? ""
            }</span>}
        </Fragment>
    );
};

export const IpAddressComponent = (props: ICommonInputComponentProps) => {

    const [answerValue, setAnswerValue] = useState<string[]>(props.question.answer ? Array.isArray(props.question.answer) ? props.question.answer : props.question.answer.split(", ") : []);
    const [error, setError] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const ChangeValue = (
        event: React.SyntheticEvent,
        value: string[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string>
    ) => {
        console.log("Printing value", event, value, reason, details);
        setAnswerValue(value);
        props.UpdateAnswer(value.join(", "));
        if (props.question.questionRegex) {
            let flagFoundInvalid = false;
            for(let i=0; i<value.length; i++) {
                // @ts-ignore
                if(ValidateAnswerWithRegex(props.question.questionRegex, details?.option)) continue;
                else {
                    flagFoundInvalid = true;
                    break;
                }
            }
            const flagValidAnswer = !flagFoundInvalid;
            if (!flagValidAnswer) setError(true);
            else if(value.length === 0) setError(true);
            else {
                setError(false);
            }
        }
    }

    return (
        <Fragment>
            <Autocomplete
                freeSolo={true}
                multiple={true}
                value={answerValue}
                onChange={ChangeValue}
                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                options={[]}
                filterOptions={(o, params) => {
                    if (params.inputValue === "") return [];
                    return [`${params.inputValue}`];
                }}
                renderOption={(props, option) => {
                    const {
                        key,
                        ...optionProps
                    } = props;
                    return (<li key={key} {...optionProps}>
                            Add {option}
                        </li>);
                }}
                key={answerValue.join(',')} 
                onBlur={() => {
                    if (inputValue && inputValue.trim() && !answerValue.includes(inputValue.trim())) {
                        const newValue = [...answerValue, inputValue.trim()];
                        ChangeValue({} as React.SyntheticEvent, newValue, "selectOption", undefined);
                        setInputValue(""); // Clear input
                    }
                }}
                className={styles.ip_address_input}
                aria-multiline={true}
                disableClearable={true}
                disabled={!props.question.isActive}
                renderInput={params => {
                    return (<TextField
                            {...params}
                            className={`${params.InputProps.className} ${error ? styles.error_box : ''}`}
                            name={"multiple"}
                        />);
                }}
            />
            {error && <span className={styles.error_message}>{props.question.questionValidationMessage}</span>}
        </Fragment>
    );
};
export const EmailComponent = (props: ICommonInputComponentProps) => {

    const [answerValue, setAnswerValue] = useState("");
    const [error, setError] = useState(false);

    const UpdateAnswerValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAnswerValue(value);
        props.UpdateAnswer(value);
        if (props.question.questionRegex) {
            const flagValidValue = ValidateAnswerWithRegex(props.question.questionRegex, value);
            if (flagValidValue) {
                setError(false);
            }
            else setError(true);
        }
    };

    return (
        <Fragment>
            <input
                type={"email"}
                value={answerValue}
                onChange={UpdateAnswerValue}
                className={`${styles.string_input} ${error ? styles.error_box : ""}`}
            />
            {error && <span className={styles.error_message}>{props.question.questionValidationMessage}</span>}
        </Fragment>
    );
};
export const PhoneComponent = (props: ICommonInputComponentProps) => {

    const [answerValue, setAnswerValue] = useState("+1 ");
    const [error, setError] = useState(false);

    const FlagValidInputEntered = (value: string) => {
        const valueToCheck = value.split("+1 ")[1].trim();
        const regex = /^[0-9 ]*$/;
        return regex.test(valueToCheck);
    };

    const UpdateAnswerValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value as string;
        if(value.startsWith("+1 ") && FlagValidInputEntered(value)) {
            const valueArray = value.split("+1 ");
            value = valueArray[valueArray.length - 1].trim();

            setAnswerValue("+1 " + value);
            props.UpdateAnswer(`+1 ${value}`);
            if (props.question.questionRegex) {
                const flagValidAnswer = ValidateAnswerWithRegex(props.question.questionRegex, value);
                if (flagValidAnswer) {
                    setError(false);
                }
                else setError(true);
            }
        }
    };

    return (
        <Fragment>
            <input
                type={"tel"}
                value={`${answerValue}`}
                className={`${styles.string_input} ${error ? styles.error_box : ""}`}
                onChange={UpdateAnswerValue}
                maxLength={16}
            />
            {error && <span className={styles.error_message}>{props.question.questionValidationMessage}</span>}
        </Fragment>
    );
};